import useLocalStorage from "../hooks/useLocalStorage";
import IChatMessage from "../types/interfaces/IChatMessage";
import { Alert, Box, IconButton, Snackbar, Toolbar } from "@mui/material";
import ComposeArea, { ComposeAreaProps } from "./chat/ComposeArea";
import ChatHistory, { ChatHistoryProps } from "./chat/ChatHistory";
import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { sendEnhancedGatewayRequest, sendRAGSearchRequest, sendDocumentCompletionRequest, sendRAGRequest } from "../functions/requests/ragRequests";
import { useOidcAccessToken } from "@axa-fr/react-oidc";
import readMessageStream from "../functions/requests/readMessageStream";
import { MessageRole } from "../types/enums/MessageRole";
import ConversationSettings from "./ConversationSettings";
import IConversationSettings, { DocumentChunk } from "../types/interfaces/IConversationSettings";
import { Close } from "@mui/icons-material";
import { useLiveQuery } from "dexie-react-hooks";
import { v4 as uuid4 } from "uuid";
import { db } from "../database/db";
import sendImageGatewayRequest from "../functions/requests/sendImageGatewayRequest";
import { withTransaction } from "@elastic/apm-rum-react";
import { apm } from "@elastic/apm-rum";
import getDefaultModelFromList from "../functions/comparisons-arrays/getDefaultModelFromList";
import { OidcConfigurationNameContext } from "../contexts/OidcConfigurationNameContext";
import { DarkModeContext } from "../contexts/DarkModeContext";
import { useSending } from "../hooks/useSending";
import { ImpersonationContext } from "../contexts/ImpersonationContext";
import { useAvailableModels } from "../hooks/useAvailableModels";
import { useSidebarOpen } from "../hooks/useSidebarOpen";
import { useFormFactor } from "../hooks/useFormFactor";
import { FormFactor } from "../types/enums/FormFactor";
import { useChatOptions } from "../hooks/useChatOptions";
import { nameConversation } from "../functions/requests/nameConversation";

export default withTransaction(
  "ActiveConversationWindow",
  "component",
)(ActiveConversationWindow);

export type ActiveConversationWindowProps = {
  conversationId: string | null | undefined;
  onTranscriptDownloaded: (history: IChatMessage[]) => void;
  defaultSystemPrompt: string;
  useRagByDefault: boolean;
};

function ActiveConversationWindow({
  conversationId,
  onTranscriptDownloaded,
  defaultSystemPrompt,
  useRagByDefault,
}: ActiveConversationWindowProps) {
  const chatMessages = useLiveQuery(
    () =>
      db.chatMessage
        .where("conversationId")
        .equals(conversationId ?? "")
        .sortBy("updated"),
    [conversationId],
  );
  const { availableModels } = useAvailableModels();
  const { isSending, setSending, clearSending } = useSending();
  const { sidebarOpen, sidebarWidth } = useSidebarOpen();
  const formFactor = useFormFactor();

  const [conversationSettings, setConversationSettings] =
    useLocalStorage<IConversationSettings>("chatSettings-" + conversationId, {
      model: getDefaultModelFromList(availableModels),
      systemPrompt: defaultSystemPrompt,
      rag: [],
      pii: false,
      top_k: 3,
      ragChunks: [],
      ragTemplate: "default",
      includeMetadata: true,
      includeScores: false,
    });
  const [stream, setStream] = useState<ChatHistoryProps["stream"]>();
  const [conversationSettingsOpen, setConversationSettingsOpen] =
    useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarText, setSnackbarText] = useState<string>("No notification");
  const { darkModeEnabled } = useContext(DarkModeContext);
  const { oidcConfigurationName } = useContext(OidcConfigurationNameContext);
  const { currentImpersonation } = useContext(ImpersonationContext);
  const abortControllerRef = useRef<AbortController | null>(null);
  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);
  const streamedContentRef = useRef<string>("");
  const accessToken = useOidcAccessToken(oidcConfigurationName);
  const { chatOptions } = useChatOptions();

  const updateSystemPrompt = useCallback(
    (newPrompt: string) => {
      (async function () {
        if (conversationId === null || conversationId === undefined) {
          return;
        }
        try {
          const systemMessage = await db.chatMessage.get({
            role: MessageRole.System,
            conversationId: conversationId,
          });
          if (systemMessage === undefined) {
            console.error(
              `No such message: System message for ${conversationId}`,
            );
            return;
          }
          systemMessage.content = newPrompt;
          await db.chatMessage.put(systemMessage, systemMessage.id);
        } catch (error) {
          console.log(error);
        }
      })();
    },
    [conversationId],
  );

  useEffect(() => {
    document.documentElement.scrollTop = 0;
  }, [conversationId]);

  useEffect(() => {
    updateSystemPrompt(conversationSettings.systemPrompt);
  }, [conversationSettings.systemPrompt, updateSystemPrompt]);

  const updateStreamedMessageText = (chunk: string) => {
    streamedContentRef.current = streamedContentRef.current += chunk;
  };

  const handleDocumentChunks = useCallback((chunks: DocumentChunk[]) => {
    if (chunks && chunks.length > 0) {
      console.log("Received document chunks:", chunks.length);
      
      // Accumulate chunks instead of replacing them
      setConversationSettings(prev => {
        const existingChunks = prev.ragChunks || [];
        
        // Check for duplicates by ID to avoid adding the same chunk multiple times
        const newChunks = chunks.filter(newChunk => 
          !existingChunks.some(existing => existing.id === newChunk.id)
        );
        
        if (newChunks.length > 0) {
          console.log("Adding", newChunks.length, "new chunks to existing", existingChunks.length, "chunks");
          return {
            ...prev,
            ragChunks: [...existingChunks, ...newChunks]
          };
        }
        
        return prev;
      });
    }
  }, [setConversationSettings]);

  const handleRegenerateMessage = async () => {
    let tr = apm.startTransaction("regenerate", "llm.message");
    let sp = tr?.startSpan("message");
    streamedContentRef.current = "";
    
    // Clear existing RAG chunks for new request
    setConversationSettings(prev => ({
      ...prev,
      ragChunks: []
    }));
    
    const lastAssistantMessageQuery = await db.chatMessage.filter(
      (m) =>
        m.conversationId === conversationId && m.role === MessageRole.Assistant,
    );
    const lastAssistantMessage = (
      await lastAssistantMessageQuery.sortBy("updated")
    ).pop();
    if (lastAssistantMessage === undefined) {
      return;
    }
    await db.chatMessage.delete(lastAssistantMessage.id);
    const selectedModel =
      typeof conversationSettings.model !== "string"
        ? conversationSettings.model
        : availableModels.find((llm) =>
            // @ts-ignore
            llm.aliases.includes(conversationSettings.model),
          ) ?? getDefaultModelFromList(availableModels);
    const assistantMessagePromise = !selectedModel.image_model
      ? sendEnhancedGatewayRequest(
          (chatMessages ?? []).slice(0, -1),
          selectedModel,
          accessToken,
          conversationSettings.rag,
          conversationSettings.pii,
          chatOptions.show_rag,
          handleSnackbarOpen,
          conversationSettings.top_k,
          conversationSettings.ragChunks,
          conversationSettings.ragTemplate,
          conversationSettings.includeMetadata,
          conversationSettings.includeScores,
          currentImpersonation,
        )
      : sendImageGatewayRequest(
          (chatMessages ?? []).slice(0, -1),
          selectedModel,
          accessToken,
          conversationSettings.rag,
          conversationSettings.pii,
          handleSnackbarOpen,
        );
    abortControllerRef.current = assistantMessagePromise.abort;
    const newId = uuid4();
    const assistantMessage = assistantMessagePromise
      .then((response) => {
        if (response.body === null) {
          throw new Error("Couldn't get message from gateway");
        } else if (!response.ok) {
          return response.json();
        }
        const [stream1, stream2] = response.body.tee();
        setStream(stream1);
        const modelName = (conversationSettings.model?.internal_name || "");
        return readMessageStream(stream2, updateStreamedMessageText, modelName, handleDocumentChunks);
      })
      .then((result): IChatMessage => {
        if (typeof result.chunks !== "string") {
          return {
            id: newId,
            conversationId: conversationId ?? "",
            role: MessageRole.Assistant,
            content: "Something went wrong: " + (result as any).detail!,
            updated: new Date(),
            model:
              conversationSettings.model ??
              getDefaultModelFromList(availableModels),
            hasFeedback: false,
          };
        }
        return {
          role: MessageRole.Assistant,
          content: result.chunks,
          citations: result?.citations,
          id: newId,
          conversationId: conversationId ?? "",
          updated: new Date(),
          model:
            conversationSettings.model ??
            getDefaultModelFromList(availableModels),
          hasFeedback: false,
        };
      })
      .catch((err) => {
        console.log(err);
        return {
          id: newId,
          conversationId: conversationId ?? "",
          role: MessageRole.Assistant,
          content: err.toString(),
          updated: new Date(),
          model:
            conversationSettings.model ??
            getDefaultModelFromList(availableModels),
          hasFeedback: false,
        };
      });

    setSending();
    assistantMessage.then((assistantMessageReceived) => {
      db.chatMessage.add(assistantMessageReceived).then(clearSending);
      if (sp) sp.end();
      if (tr) tr.end();
    });
  };

  const handleSendMessage = useCallback<ComposeAreaProps["onSend"]>(
    async (newMessage: IChatMessage) => {
      if (!conversationId || !chatMessages) return;

      await db.chatMessage.add(newMessage);
      setSending();
      let foundChunks: DocumentChunk[] = [];
      let noDocsFound: boolean = false;
      try {
        const selectedModel =
          typeof conversationSettings.model !== "string"
            ? conversationSettings.model
            : availableModels.find((llm) =>
                // @ts-ignore
                llm.aliases.includes(conversationSettings.model),
              ) ?? getDefaultModelFromList(availableModels);

        // If RAG is enabled, first make a search request
        if (conversationSettings.rag.length > 0 && (!conversationSettings.ragChunks || conversationSettings.ragChunks.length === 0)) {
          const searchResponse = await sendRAGSearchRequest(
            chatMessages.concat(newMessage),
            selectedModel,
            accessToken,
            {
              rag: conversationSettings.rag,
              top_k: conversationSettings.top_k || 1,
              chunks: conversationSettings.ragChunks,
              ragTemplate: conversationSettings.ragTemplate,
              includeMetadata: conversationSettings.includeMetadata,
              includeScores: conversationSettings.includeScores,
              showRag: true,
            },
            currentImpersonation
          );

          if (searchResponse.ok) {
            const searchData = await searchResponse.json();
            // Update conversation settings with the search results
            foundChunks = searchData.documents.map((doc: any) => ({
                id: doc.id,
                document_title: doc.document_title,
                document_source: doc.document_source,
                source_index: doc.source_index,
                document_id: doc.document_id,
                url: doc.url,
                content: doc.content,
                score: doc.score,
                content_length: doc.content_length,
                metadata: doc.metadata
              }))

            setConversationSettings(prev => ({
              ...prev,
              ragChunks: foundChunks}));
            if (!foundChunks || foundChunks.length === 0) {
              noDocsFound = true;
            }
          }
        }

        // Now make the completion request
        const chunksToUse = conversationSettings.ragChunks && conversationSettings.ragChunks.length > 0 
          ? conversationSettings.ragChunks 
          : foundChunks;

        // If no documents were found under DLS/RBAC, add an explicit notice before answering generally
        if (noDocsFound) {
          const noticeId = uuid4().toString();
          const noticeMessage: IChatMessage = {
            id: noticeId,
            conversationId: conversationId,
            role: MessageRole.Assistant,
            content:
              "No relevant documents were found for your account in the selected data sources (DLS/RBAC enforced). I will answer without document context.",
            updated: new Date(),
            model: selectedModel,
            hasFeedback: false,
          };
          await db.chatMessage.add(noticeMessage);
        }

        const assistantMessagePromise = !selectedModel.image_model
          ? (chunksToUse.length > 0
              ? sendDocumentCompletionRequest(
                  chatMessages.concat(newMessage),
                  selectedModel,
                  accessToken,
                  {
                    rag: conversationSettings.rag,
                    top_k: conversationSettings.top_k || 1,
                    chunks: chunksToUse,
                    ragTemplate: conversationSettings.ragTemplate,
                    includeMetadata: conversationSettings.includeMetadata,
                    includeScores: conversationSettings.includeScores,
                    showRag: true,
                  },
                  handleSnackbarOpen,
                  currentImpersonation,
                )
              : sendEnhancedGatewayRequest(
                  chatMessages.concat(newMessage),
                  selectedModel,
                  accessToken,
                  conversationSettings.rag,
                  conversationSettings.pii,
                  chatOptions.show_rag,
                  handleSnackbarOpen,
                  conversationSettings.top_k,
                  conversationSettings.ragChunks,
                  conversationSettings.ragTemplate,
                  conversationSettings.includeMetadata,
                  conversationSettings.includeScores,
                  currentImpersonation,
                ))
          : sendImageGatewayRequest(
              chatMessages.concat(newMessage),
              selectedModel,
              accessToken,
              conversationSettings.rag,
              conversationSettings.pii,
              handleSnackbarOpen,
            );

        abortControllerRef.current = assistantMessagePromise.abort;
        const newId = uuid4().toString();
        const assistantMessage: IChatMessage = {
          id: newId,
          conversationId: conversationId,
          role: MessageRole.Assistant,
          content: "",
          updated: new Date(),
          model: selectedModel,
          hasFeedback: false,
        };

        assistantMessagePromise.then(async (response: Response) => {
          if (response.ok) {
            if (response.body === null) {
              throw new Error("Couldn't get message from gateway");
            }
            const [stream1, stream2] = response.body.tee();
            setStream(stream1);
            const modelName = selectedModel.internal_name;
            const result = await readMessageStream(stream2, updateStreamedMessageText, modelName, handleDocumentChunks);
            
            const receivedMessage: IChatMessage = {
              id: newId,
              conversationId: conversationId,
              role: MessageRole.Assistant,
              content: result.chunks,
              citations: result.citations,
              updated: new Date(),
              model: selectedModel,
              hasFeedback: false,
            };
            await db.chatMessage.add(receivedMessage);
          } else {
            const errorData = await response.json();
            const errorMessage: IChatMessage = {
              id: newId,
              conversationId: conversationId,
              role: MessageRole.Assistant,
              content: `Error: ${errorData.detail || response.statusText}`,
              updated: new Date(),
              model: selectedModel,
              hasFeedback: false,
            };
            await db.chatMessage.add(errorMessage);
          }
          clearSending();
        }).catch((error: any) => {
          console.error("Error processing response:", error);
          const errorMessage: IChatMessage = {
            id: newId,
            conversationId: conversationId,
            role: MessageRole.Assistant,
            content: `Error: ${error.message || "Failed to process response"}`,
            updated: new Date(),
            model: selectedModel,
            hasFeedback: false,
          };
          db.chatMessage.add(errorMessage).then(clearSending);
        });
      } catch (error) {
        console.error("Error sending message:", error);
        clearSending();
      }
    },
    [
      conversationId,
      chatMessages,
      conversationSettings,
      availableModels,
      accessToken,
      chatOptions.show_rag,
      clearSending,
      setSending,
      setConversationSettings,
    ],
  );

  const handleConversationSettingsOpen = () => {
    setConversationSettingsOpen(true);
  };

  const handleConversationSettingsClose = () => {
    setConversationSettingsOpen(false);
  };

  const handleConversationSettingsSave = (
    newSettings: IConversationSettings,
  ) => {
    setConversationSettings(newSettings);
  };

  const handleExportTranscript = () => {
    onTranscriptDownloaded(chatMessages ?? []);
  };

  const handleRemoveChunk = useCallback((chunkId: string) => {
    setConversationSettings(prev => ({
      ...prev,
      ragChunks: (prev.ragChunks || []).filter(chunk => chunk.id !== chunkId)
    }));
  }, [setConversationSettings]);

  const handleClearAllChunks = useCallback(() => {
    setConversationSettings(prev => ({
      ...prev,
      ragChunks: []
    }));
  }, [setConversationSettings]);

  const cancelStream = useCallback(() => {
    setStream(undefined);
    clearSending();
    if (abort != null) {
      try {
        abort();
      } catch (error) {
        console.log("Aborted.");
      }
    }
  }, [clearSending, abort]);

  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSnackbarOpen = (notificationText: string) => {
    setSnackbarText(notificationText);
    setSnackbarOpen(true);
  };

  const composeBarHeight = 64;

  async function getNamed(id: string): Promise<boolean | undefined> {
    try {
      const conversation = await db.conversationInfo.get(id);
      return conversation?.named;
    } catch (error) {
      console.error("Failed to fetch named value:", error);
      return undefined;
    }
  }

  const namingInProgress = useRef(false);

  useEffect(() => {
    if (
      conversationId &&
      chatMessages &&
      chatMessages.length > 1 &&
      !namingInProgress.current
    ) {
      (async () => {
        namingInProgress.current = true;
        const isNamed = await getNamed(conversationId);
        if (!isNamed) {
          try {
            const selectedModel =
              typeof conversationSettings.model !== "string"
                ? conversationSettings.model
                : availableModels.find((llm) =>
                    // @ts-ignore
                    llm.aliases.includes(conversationSettings.model),
                  ) ?? getDefaultModelFromList(availableModels);
            const response = await nameConversation(
              chatMessages.slice(1) ?? [],
              selectedModel,
              getDefaultModelFromList(availableModels),
              accessToken,
            );
            if (response.ok) {
              const result = await response.json();
              let updatedName: string =
                result["choices"][0]["message"]["content"];
              if (updatedName.includes(": ")) {
                updatedName = updatedName.split(": ")[1];
              }
              updatedName = updatedName.replaceAll('"', "");
              const now = new Date();
              await db.conversationInfo.put({
                id: conversationId,
                name: updatedName,
                updated: now,
                named: true,
              });
            } else {
              console.error("Failed to automatically name the conversation");
            }
          } catch (error) {
            console.error(
              "Error in automatically naming the conversation:",
              error,
            );
          } finally {
            namingInProgress.current = false;
          }
        } else {
          namingInProgress.current = false;
        }
      })();
    }
  }, [chatMessages]);

  if (conversationId == null) {
    return (
      <>
        <Toolbar />
        <Alert
          severity="info"
          sx={{ ml: "auto", mr: "auto", mt: "1em", width: "fit-content" }}
        >
          Select or add a conversation to begin.
        </Alert>
      </>
    );
  }

  return (
    <>
      <Toolbar />
      <Box component="div">
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          message={snackbarText}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          action={
            <Fragment>
              <IconButton
                aria-label="close"
                color="inherit"
                sx={{ p: 0.5 }}
                onClick={handleSnackbarClose}
              >
                <Close />
              </IconButton>
            </Fragment>
          }
        />
        <ChatHistory
          chatMessages={chatMessages ?? []}
          stream={stream}
          onRegenerateMessage={handleRegenerateMessage}
          heightOffset={composeBarHeight}
          model={conversationSettings.model}
          defaultSystemPrompt={defaultSystemPrompt}
        />
        <Box
          position="fixed"
          component="div"
          sx={{
            boxSizing: "border-box",
            minHeight: composeBarHeight,
            maxHeight: `calc(${composeBarHeight}px * 3)`,
            bottom: 0,
            right:
              formFactor > FormFactor.Tablet && chatOptions.use_promotions
                ? "280px"
                : 0,
            left: sidebarOpen ? sidebarWidth : 0,
            margin: 0,
            backgroundColor: darkModeEnabled ? "black" : "white",
            overflow: "none",
          }}
        >
          <ComposeArea
            onSend={handleSendMessage}
            enabled={!isSending}
            onConversationSettingsOpen={handleConversationSettingsOpen}
            onExportTranscript={handleExportTranscript}
            onError={handleSnackbarOpen}
            conversationId={conversationId ?? ""}
            cancelStream={cancelStream}
            ragChunks={conversationSettings.ragChunks}
            onRemoveChunk={handleRemoveChunk}
            onClearAllChunks={handleClearAllChunks}
          />
        </Box>
        <ConversationSettings
          open={conversationSettingsOpen}
          onClose={handleConversationSettingsClose}
          onSave={handleConversationSettingsSave}
          currentRag={conversationSettings.rag}
          currentPii={conversationSettings.pii}
          currentModel={conversationSettings.model}
          currentSystemPrompt={conversationSettings.systemPrompt}
          defaultSystemPrompt={defaultSystemPrompt}
          currentTopK={conversationSettings.top_k || 3}
          ragChunks={conversationSettings.ragChunks || []}
        />
      </Box>
    </>
  );
}
